<script setup lang="ts">
import { PaginationData } from '@/@types/order'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { TableBody, TableCell } from '@/components/ui/table-custom'
import Table from '@/components/ui/table-custom/Table.vue'
import TableHead from '@/components/ui/table-custom/TableHead.vue'
import TableHeader from '@/components/ui/table-custom/TableHeader.vue'
import TableRow from '@/components/ui/table-custom/TableRow.vue'
import { Title } from '@/components/ui/title'
import { formatDateFull } from '@/helpers/format-date'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import { ArrowLeftCircle, Search } from 'lucide-vue-next'
import { computed } from 'vue'

export interface AdminUserIndexRow {
    id: number
    name: string | null
    email: string
    sso_user_id: number | null
    import_logs_count: number
    created_at: string
}

const props = defineProps<{
    users: AdminUserIndexRow[]
    pagination: PaginationData
    filters: {
        q?: string | null
        with_logs?: string | null
    }
}>()

const form = useForm({
    q: props.filters.q || '',
    with_logs: props.filters.with_logs || '',
})

function applyFilters() {
    form.get('/admin/users', { preserveScroll: true })
}

const paginationBase = computed(() => {
    const p = new URLSearchParams()
    if (form.q) p.set('q', form.q)
    if (form.with_logs) p.set('with_logs', form.with_logs)
    return p.toString()
})

function pageHref(page: number) {
    const p = new URLSearchParams(paginationBase.value)
    if (page > 1) p.set('page', String(page))
    const qs = p.toString()
    return qs ? `/admin/users?${qs}` : '/admin/users'
}
</script>

<template>
    <Head title="Usuários e logs de importação" />

    <LightLayout contentClasses="bg-slate-50">
        <main class="container flex flex-col gap-8">
            <Title class="flex items-center gap-2 font-sans font-semibold">
                <Link href="/"> <ArrowLeftCircle class="h-8 w-8" stroke-width="1.2" /> </Link>
                Usuários e logs de importação
            </Title>

            <p class="text-muted-foreground -mt-4 max-w-3xl text-sm">
                Consulte usuários e o histórico retornado pelo CAS ao importar participantes (planilha).
            </p>

            <Card class="border-0 pt-2 shadow-sm">
                <CardContent class="space-y-6">
                    <form
                        class="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end"
                        @submit.prevent="applyFilters"
                    >
                        <div class="min-w-[200px] flex-1">
                            <label class="text-muted-foreground mb-1 block text-sm font-medium" for="users-q">Nome ou e-mail</label>
                            <div class="relative">
                                <Search
                                    class="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2"
                                />
                                <Input id="users-q" v-model="form.q" type="text" placeholder="Buscar…" class="pl-10" />
                            </div>
                        </div>
                        <div class="w-full min-w-[200px] md:w-56">
                            <label class="text-muted-foreground mb-1 block text-sm font-medium" for="users-with-logs">Filtro</label>
                            <select
                                id="users-with-logs"
                                v-model="form.with_logs"
                                class="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            >
                                <option value="">Todos</option>
                                <option value="1">Só com log de importação</option>
                            </select>
                        </div>
                        <div class="flex gap-2">
                            <Button type="submit" :disabled="form.processing">Filtrar</Button>
                            <Button variant="outline" :as="Link" href="/admin/users"> Limpar </Button>
                        </div>
                    </form>

                    <div v-if="users.length === 0" class="text-muted-foreground rounded-lg border border-dashed py-12 text-center">
                        Nenhum usuário encontrado com os filtros atuais.
                    </div>

                    <Table v-else class="lg:table-fixed">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Usuário</TableHead>
                                <TableHead>E-mail</TableHead>
                                <TableHead>ID SSO (CAS)</TableHead>
                                <TableHead class="w-28">Logs</TableHead>
                                <TableHead>Cadastro</TableHead>
                                <TableHead class="w-32"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="u in users" :key="u.id">
                                <TableCell class="font-medium">{{ u.name }}</TableCell>
                                <TableCell>{{ u.email }}</TableCell>
                                <TableCell>{{ u.sso_user_id ?? '—' }}</TableCell>
                                <TableCell>
                                    <Badge variant="secondary" class="rounded-full px-2.5 font-semibold tabular-nums">
                                        {{ u.import_logs_count }}
                                    </Badge>
                                </TableCell>
                                <TableCell>{{ formatDateFull(u.created_at, $i18n.locale) }}</TableCell>
                                <TableCell>
                                    <Button size="sm" variant="secondary" :as="Link" :href="`/admin/users/${u.id}`">
                                        Ver logs
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <nav
                        v-if="pagination.last_page > 1"
                        class="flex flex-wrap items-center justify-center gap-2 border-t pt-6"
                        aria-label="Paginação"
                    >
                        <Button v-if="pagination.current_page > 1" variant="outline" size="sm" :as="Link" :href="pageHref(1)">
                            Primeira
                        </Button>
                        <Button v-else variant="outline" size="sm" disabled> Primeira </Button>

                        <Button
                            v-if="pagination.current_page > 1"
                            variant="outline"
                            size="sm"
                            :as="Link"
                            :href="pageHref(pagination.current_page - 1)"
                        >
                            Anterior
                        </Button>
                        <Button v-else variant="outline" size="sm" disabled> Anterior </Button>

                        <span class="text-muted-foreground px-2 text-sm tabular-nums">
                            {{ pagination.current_page }} / {{ pagination.last_page }}
                        </span>

                        <Button
                            v-if="pagination.current_page < pagination.last_page"
                            variant="outline"
                            size="sm"
                            :as="Link"
                            :href="pageHref(pagination.current_page + 1)"
                        >
                            Próxima
                        </Button>
                        <Button v-else variant="outline" size="sm" disabled> Próxima </Button>

                        <Button
                            v-if="pagination.current_page < pagination.last_page"
                            variant="outline"
                            size="sm"
                            :as="Link"
                            :href="pageHref(pagination.last_page)"
                        >
                            Última
                        </Button>
                        <Button v-else variant="outline" size="sm" disabled> Última </Button>
                    </nav>
                </CardContent>
            </Card>
        </main>
    </LightLayout>
</template>
